import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { onAuthStateChanged, User, signOut } from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';

interface AuthContextType {
  currentUser: User | null;
  userRole: string | null;
  loading: boolean;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    let authStateDetermined = false;

    // Check if we just logged out - check URL parameter and sessionStorage
    const urlParams = new URLSearchParams(window.location.search);
    const hasLogoutParam = urlParams.has('logout');
    const logoutFlag = sessionStorage.getItem('__logout_flag__');
    const forceLogout = sessionStorage.getItem('__force_logout__');
    
    // If logout was detected, clear state but STILL set up auth listener for future logins
    if (logoutFlag || forceLogout || hasLogoutParam) {
      console.log('AuthContext: Logout detected (flag or URL param), clearing auth state');
      
      // Clear flags but keep a marker that we logged out (for timeout check)
      if (logoutFlag || forceLogout) {
        sessionStorage.removeItem('__logout_flag__');
        sessionStorage.removeItem('__force_logout__');
      }
      
      // Set a marker that persists for the timeout check
      sessionStorage.setItem('__recent_logout__', Date.now().toString());
      
      // Clear state immediately
      setCurrentUser(null);
      setUserRole(null);
      setLoading(false);
      
      // Also clear any remaining Firebase auth state
      try {
        Object.keys(localStorage).forEach(key => {
          if (key.toLowerCase().includes('firebase') || key.toLowerCase().includes('auth')) {
            localStorage.removeItem(key);
          }
        });
      } catch (e) {
        console.warn('Error clearing storage in AuthContext:', e);
      }
      
      // Clean up URL parameter after a moment
      if (hasLogoutParam) {
        setTimeout(() => {
          const newUrl = window.location.pathname;
          window.history.replaceState({}, '', newUrl);
        }, 100);
      }
      
      // IMPORTANT: Don't return early - we need to set up the auth listener
      // so that future logins can be detected. The listener will ignore the current
      // auth state if logout flags exist, but will work for new logins.
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!isMounted) return;
      
      console.log('AuthContext: Auth state changed, user:', user?.uid || 'null');
      
      // Check for recent logout - block auto-restore but allow new logins
      const recentLogout = sessionStorage.getItem('__recent_logout__');
      if (recentLogout && user) {
        const logoutTime = parseInt(recentLogout);
        const timeSinceLogout = Date.now() - logoutTime;
        
        // If logout was very recent (within 30 seconds) AND user exists, this is likely auto-restore
        // Block it to prevent Firebase from restoring the user after logout
        if (timeSinceLogout < 30000) {
          console.log('AuthContext: ⚠️ Recent logout detected with user present, blocking auto-restore (time since logout:', timeSinceLogout, 'ms)');
          console.log('AuthContext: User ID attempting to restore:', user.uid);
          
          // Force sign out again to prevent restoration
          try {
            await signOut(auth);
            console.log('AuthContext: Force signed out user to prevent auto-restore');
          } catch (signOutError) {
            console.warn('AuthContext: Error force signing out:', signOutError);
          }
          
          // Clear state to ensure user stays logged out
          setCurrentUser(null);
          setUserRole(null);
          setLoading(false);
          
          // Don't remove the marker yet - keep it to block further auto-restore attempts
          return;
        } else {
          // More than 30 seconds passed, this is likely a new login
          console.log('AuthContext: ✅ Time since logout:', timeSinceLogout, 'ms - allowing new login');
          sessionStorage.removeItem('__recent_logout__');
        }
      } else if (recentLogout && !user) {
        // User is null and logout was recent - this is a legitimate sign-out
        console.log('AuthContext: ✅ User is null after logout - legitimate sign-out');
        // Keep the marker for a bit longer to prevent auto-restore
        const logoutTime = parseInt(recentLogout);
        const timeSinceLogout = Date.now() - logoutTime;
        if (timeSinceLogout > 30000) {
          sessionStorage.removeItem('__recent_logout__');
        }
      }
      
      // Double-check logout flag in case it was set during this callback
      const currentLogoutFlag = sessionStorage.getItem('__logout_flag__');
      const currentForceLogout = sessionStorage.getItem('__force_logout__');
      
      if (currentLogoutFlag || currentForceLogout) {
        console.log('AuthContext: Logout flag detected in auth callback, ignoring user and clearing state');
        sessionStorage.removeItem('__logout_flag__');
        sessionStorage.removeItem('__force_logout__');
        setCurrentUser(null);
        setUserRole(null);
        setLoading(false);
        return;
      }
      
      try {
        authStateDetermined = true;
        setCurrentUser(user);
        if (user) {
          // User is signed in, fetch their role from Firestore with timeout
          try {
            console.log('AuthContext: Fetching role for user:', user.uid);
            const userDocRef = doc(db, 'users', user.uid);
            // Add timeout to prevent hanging on slow Firestore
            const fetchRolePromise = getDoc(userDocRef);
            const timeoutPromise = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Role fetch timeout')), 5000) // Increased to 5 seconds
            );
            
            const userDocSnap = await Promise.race([fetchRolePromise, timeoutPromise]) as any;
            if (userDocSnap && userDocSnap.exists()) {
              const data = userDocSnap.data();
              const role = data.role;
              // Block removed test admin account from retaining admin access
              const adminBlocklistEmails = ['test@gmail.com'];
              const email = (user?.email || '').toLowerCase();
              const isBlocked = adminBlocklistEmails.some((e) => email === e.toLowerCase());
              if (role === 'admin' && isBlocked) {
                console.log('AuthContext: Blocked admin access for removed test account:', email);
                setUserRole(null);
              } else {
                console.log('AuthContext: Role fetched successfully:', role);
                setUserRole(role);
              }
            } else {
              console.log('AuthContext: User document does not exist, setting role to null');
              setUserRole(null);
            }
          } catch (roleError: any) {
            // Don't block app rendering if role fetch fails - set to null and continue
            if (roleError?.message === 'Role fetch timeout') {
              console.warn('AuthContext: Role fetch timeout (non-critical) - user can still use the app');
            } else {
              console.warn('AuthContext: Error fetching user role (non-critical):', roleError);
            }
            setUserRole(null);
          }
        } else {
          // User is signed out
          setUserRole(null);
        }
      } catch (error) {
        console.error('Error in auth state change:', error);
        // Even if there's an error, we should still render the app
        if (isMounted) {
          setCurrentUser(null);
          setUserRole(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    });

    // Set a timeout as a safety net (only if auth state hasn't been determined)
    // Firebase auth state should persist and load quickly, but we need a fallback
    const timeout = setTimeout(() => {
      if (isMounted && !authStateDetermined && loading) {
        // Check for recent logout (within last 10 seconds) before showing timeout warning
        const recentLogout = sessionStorage.getItem('__recent_logout__');
        const hasLogoutParam = new URLSearchParams(window.location.search).has('logout');
        
        if (recentLogout || hasLogoutParam) {
          // Recent logout detected, don't show timeout warning
          const logoutTime = recentLogout ? parseInt(recentLogout) : Date.now();
          const timeSinceLogout = Date.now() - logoutTime;
          if (timeSinceLogout < 10000) { // Within 10 seconds
            console.log('AuthContext: Timeout reached but recent logout detected, skipping timeout warning');
            // Clean up the marker
            sessionStorage.removeItem('__recent_logout__');
            return;
          }
        }
        
        // No recent logout, show timeout warning
        console.warn('Firebase auth timeout, rendering app anyway');
        setLoading(false);
      }
    }, 5000); // Increased to 5 seconds to give Firebase more time

    return () => {
      isMounted = false;
      unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const logout = async () => {
    try {
      console.log('Logout: Starting logout process...');
      console.log('Logout: Current user before signOut:', currentUser?.uid);
      
      // Sign out from Firebase
      try {
        await signOut(auth);
        console.log('Logout: Firebase signOut successful');
      } catch (signOutError: any) {
        console.warn('Logout: Sign out from Firebase failed:', signOutError);
        // Continue to clear state even if signOut fails
      }
      
      // Immediately clear local state
      // This must happen before the auth state listener can restore the user
      setCurrentUser(null);
      setUserRole(null);
      
      console.log('Logout: Local state cleared');
    } catch (error) {
      console.error('Error in logout:', error);
      // Even if there's an error, ensure state is cleared
      setCurrentUser(null);
      setUserRole(null);
    }
  };

  const value = {
    currentUser,
    userRole,
    loading,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
